import {useEffect} from "react";
import {useMenubarKeyPress} from "./useMenubarKeyPress";
import {useStatusHints} from "../StatusHintContext";

// A single keyboard-driven menubar action, declared in one place. Bundling the
// trigger key(s), the display token, the status hint and the handler means a
// shortcut's key never drifts from its tooltip or its hint: the RibbonButton
// reads `.display` / `.run` off the same object the key listener dispatches from.
export type MenubarShortcut = {
    // KeyboardEvent.key values that fire it (letters compared case-insensitively),
    // e.g. ["Delete", "Backspace"], ["v"], ["ArrowLeft"].
    keys: string[];
    // token shown in the RibbonButton tooltip, e.g. "Delete", "V", "←".
    display: string;
    // status-bar hint text; omit to surface none. Identical hints across several
    // shortcuts collapse to one in the status bar (e.g. "Press V or H to split").
    hint?: string;
    // by default a shortcut is ignored while meta/ctrl/alt is held so it doesn't
    // shadow browser shortcuts (paste, etc). Set true to fire regardless.
    allowModifiers?: boolean;
    run: () => void;
};

// Register a set of menubar shortcuts for as long as the calling component is
// mounted: one keydown listener dispatches to the matching action, and each
// distinct hint is published to the status bar. The list may vary between renders
// (e.g. drop the Remove shortcut when the element isn't removable).
export function useMenubarShortcuts(shortcuts: MenubarShortcut[]) {
    useMenubarKeyPress(e => {
        for(const s of shortcuts) {
            if(!s.allowModifiers && (e.metaKey || e.ctrlKey || e.altKey)) continue;
            if(!s.keys.some(k => k.toLowerCase() === e.key.toLowerCase())) continue;
            e.preventDefault();
            s.run();
            return;
        }
    });

    const ctx = useStatusHints();
    const hints = Array.from(new Set(shortcuts.map(s => s.hint).filter((h): h is string => !!h)));
    // re-register only when the set of hint texts actually changes.
    const sig = hints.join("|");

    useEffect(() => {
        if(!ctx) return;
        const unregister = hints.map(h => ctx.register(h));
        return () => unregister.forEach(u => u());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ctx, sig]);
}
